import { HttpParams } from "@angular/common/http";
import { forwardRef, Inject, Injectable } from "@angular/core";
import { firstValueFrom } from "rxjs";
import { ACTION_STATUSES, ACTION_STATUSES_BY_ID, ACTION_SYNC, USER_ACTIONS_V2, WORK_ORDERS_V2 } from "../common/endpoints";
import { ActionStatus, PagedList, WorkOrder } from "../model";
import { AuthenticationService } from "./authentication.service";
import { HttpService } from "./http.service";

@Injectable()
export class UserActionStatusService {

    constructor(
        @Inject(forwardRef(() => HttpService)) private httpService: HttpService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService
    ) { }

    patchActionStatus(actionStatus: ActionStatus, body: any, historical: boolean): Promise<ActionStatus> {
        const params = new HttpParams().set('historical', historical);
        return firstValueFrom(this.httpService.patch<ActionStatus>(ACTION_STATUSES_BY_ID.replace('{id}', actionStatus.id), body, params));
    }

    createActionStatus(body: any, historical: boolean): Promise<ActionStatus> {
        const params = new HttpParams().set('historical', historical);
        return firstValueFrom(this.httpService.post<ActionStatus>(ACTION_STATUSES, body, params));
    }

    resynchronizeActions(thingId: string, thingWorkingHours?: number): Promise<void> {
        let body: any = { thingId: thingId };
        if (thingWorkingHours) {
            body.thingWorkingHours = thingWorkingHours;
        }
        return firstValueFrom(this.httpService.post<void>(ACTION_SYNC, body));
    }

    getActionStatusById(actionStatusId: string, historical: boolean): Promise<ActionStatus> {
        let params = new HttpParams().set('historical', historical);
        params = params.set('language', this.authenticationService.getUser()?.language || navigator.language);
        return firstValueFrom(this.httpService.get<ActionStatus>(ACTION_STATUSES_BY_ID.replace('{id}', actionStatusId), params));
    }

    getPagedList(params: HttpParams): Promise<PagedList<ActionStatus>> {
        return firstValueFrom(this.httpService.get<PagedList<ActionStatus>>(USER_ACTIONS_V2, params));
    }

    getRecursivelyAllStatuses(params: HttpParams, page?: number, statuses?: ActionStatus[]): Promise<ActionStatus[]> {
        if (!page) {
            page = 0;
        }
        if (!statuses) {
            statuses = [];
        }
        params = params.set('page', page).set('size', 200);
        return this.getPagedList(params)
            .then(pagedActions => {
                statuses = statuses.concat(pagedActions.content);
                if (pagedActions.last) {
                    return statuses;
                } else {
                    return this.getRecursivelyAllStatuses(params, ++page, statuses);
                }
            });
    }

    getWorkOrderPagedList(params: HttpParams): Promise<PagedList<WorkOrder>> {
        return firstValueFrom(this.httpService.get<PagedList<WorkOrder>>(WORK_ORDERS_V2, params))
    }

    getRecursivelyAllWorkOrders(params: HttpParams, page?: number, workOrders?: WorkOrder[]): Promise<WorkOrder[]> {
        if (!page) {
            page = 0;
        }
        if (!workOrders) {
            workOrders = [];
        }
        params = params.set('page', page).set('size', 200);
        return this.getWorkOrderPagedList(params)
            .then(pagedWorkOrders => {
                workOrders = workOrders.concat(pagedWorkOrders.content);
                if (pagedWorkOrders.last) {
                    return workOrders;
                } else {
                    return this.getRecursivelyAllWorkOrders(params, ++page, workOrders);
                }
            });
    }
}